import { AbstractDialog } from "./abstract_dialog.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { DialogManager } from "./dialog_manager.js";
import { UITextElement } from "../common_ui_elements/ui_text_element.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UICountryFlag } from "../common_ui_elements/ui_country_flag.js";
import { UIAvatarSelectionItem } from "../common_ui_elements/ui_avatar_selection_item.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { ImgID } from "../../image_loader/img_id.js";
import { resolveCountry } from "../../analytics/analytics_identity.js";
import { getFlagByCountryCode } from "../../common/flag_utils.js";
import { MyTextAlign, MyTextVerticalAlign } from "../common_ui_elements/ui_text_element.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { AnalyticStore } from "../../analytics/analytic_store.js";
import { AuthSessionStore } from "../../api/auth_session.js";
import { AvatarSelectionDialog } from "./avatar_selection_dialog.js";
import { VersionLoader } from "../../version_loader.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { LinkedSocialProvider, getLinkedSocialProviders, hasLinkedSocialProvider } from "./account_providers_preview_state.js";
import { SaveDialog } from "./save_dialog.js";
import { EditNameDialog } from "./edit_name_dialog.js";
import { AccountProgressSavedDialog } from "./account_progress_saved_dialog.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { Easing } from "../../transforms/transform_base.js";
import { Vector2 } from "../../common/vector2.js";
import type { TelegramAvatarIdentity } from "../../common/telegram_avatar.js";

const PROFILE_LINE_H_DESIGN = 4;

export interface ProfileDialogPlayerData
{
  name: string;
  avatar_id: number;
  facebook_avatar_url?: string | null;
  telegram_identity?: TelegramAvatarIdentity;
  country_code: string;
  level: number;
  points: number;
  user_id?: string;
  days_in_game?: number;
  editable?: boolean;
}

export class ProfileDialog extends AbstractDialog
{
  private bg!: UINineSliceElement;
  private name_input!: UITextElement;
  private profile_avatar!: UIAvatarSelectionItem;
  private close_btn!: UIImage;
  private readonly player_data?: ProfileDialogPlayerData;
  private readonly lines_h = LayoutScaler.heightFromDesign( PROFILE_LINE_H_DESIGN );
  private static readonly NICKNAME_KEY = 'player_nickname';
  private static readonly AVATAR_KEY   = 'player_avatar_id';

  constructor( player_data?: ProfileDialogPlayerData )
  {
    const design_body_w = 909;
    const design_body_h = 891;

    const body_w = LayoutScaler.widthFromDesignAdaptive( design_body_w );
    const body_h = LayoutScaler.heightFromDesign( design_body_h );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = Math.max( 0, ( DialogManager.getHeight() - body_h ) / 2 );

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.player_data = player_data;

    this.createElements();
  }

  private createElements(): void
  {
    this.createBackground();
    this.createAvatar();
    // this.createCountryFlag();
    this.createPlayerNickname();
    this.createInfoBlock();
    this.createUserIdText();
    this.createVersionText();
    this.createCloseButton();
  }

  private createCountryFlag(): void
  {
    const design_flag_w = 94.35;
    const design_flag_h = 94.35;
    const country_code = this.player_data?.country_code
      || localStorage.getItem( 'player_country' )?.trim()
      || resolveCountry();
    const flag_w = LayoutScaler.widthFromDesign( design_flag_w );
    const flag_h = LayoutScaler.heightFromDesign( design_flag_h );
    const flag_x = this.rect.getW2() - LayoutScaler.widthFromDesign( 143 );
    const flag_y = LayoutScaler.yFromDesign( 60.15 );

    const flag = new UICountryFlag(
      this.ctx,
      flag_x,
      flag_y,
      flag_w,
      flag_h,
      getFlagByCountryCode( country_code ),
    );
    flag.setClickable( false );
    this.addChild( flag );
  }

  private createAvatar(): void
  {
    const design_avatar_size = 220;
    const design_avatar_y = 60;
    const design_edit_icon_size = 90;
    const design_edit_icon_inset_x = 56;
    const design_edit_icon_offset_y = 6;

    const avatar_size = LayoutScaler.widthFromDesign( design_avatar_size );
    const avatar_x    = this.rect.getW2() - avatar_size / 2;
    const avatar_y    = LayoutScaler.yFromDesign( design_avatar_y );

    const saved_id = this.player_data?.avatar_id
      ?? parseInt( localStorage.getItem( ProfileDialog.AVATAR_KEY ) ?? '0', 10 );
    const initial_id = isNaN( saved_id ) ? 0 : saved_id;
    const fb_avatar_url = this.player_data?.facebook_avatar_url
      ?? localStorage.getItem( 'player_facebook_avatar_url' );
    const editable = this.player_data?.editable ?? true;

    const open_avatar_selection = () =>
    {
      DialogManager.openDialog( new AvatarSelectionDialog( ( id ) =>
      {
        this.profile_avatar.setAvatarId( id, localStorage.getItem( 'player_facebook_avatar_url' ) );
      } ) );
    };

    const design_border_w = 5;
    this.profile_avatar = new UIAvatarSelectionItem(
      this.ctx,
      avatar_x,
      avatar_y,
      avatar_size,
      avatar_size,
      initial_id,
      open_avatar_selection,
      LayoutScaler.widthFromDesign( design_border_w ),
      0,
      undefined,
      false,
      {
        type: "linear-gradient",
        from: "#F9F4C9",
        to: "#B5A084",
        direction: "vertical"
      },
      fb_avatar_url,
      this.player_data?.telegram_identity
    );
    this.profile_avatar.setClickable( editable );
    this.addChild( this.profile_avatar );

    if ( editable )
    {
      const edit_icon_size = LayoutScaler.widthFromDesign( design_edit_icon_size );
      const edit_icon_x = avatar_x + avatar_size - LayoutScaler.widthFromDesign( design_edit_icon_inset_x );
      const edit_icon_y = avatar_y + LayoutScaler.heightFromDesign( design_edit_icon_offset_y );
      this.createEditButton( edit_icon_x, edit_icon_y, edit_icon_size, open_avatar_selection );
    }
  }

  private createEditButton( x: number, y: number, size: number, on_click: () => void ): void
  {
    const btn = new UIImage(
      this.ctx,
      x,
      y,
      size,
      size,
      ImgID.IconEdit,
      {},
      true
    );

    btn.setOnClick( on_click );
    this.addChild( btn );
  }

  private createVersionText(): void
  {
    const design_text_w = 230;
    const design_text_h = 58;
    const design_text_size = 40;

    const text_h = LayoutScaler.heightFromDesign( design_text_h );
    const text_w = LayoutScaler.widthFromDesign( design_text_w );
    const text_x = this.rect.getW2() - text_w / 2;
    const text_y = this.rect.getH() + LayoutScaler.yFromDesign( 450 );

    const version = VersionLoader.getVersion();

    const text = new UITextElement(
      this.ctx,
      text_x,
      text_y,
      text_w,
      text_h,
      {
        text: `Ver: ${version}`,
        font_size: LayoutScaler.textSizeFromDesign( design_text_size ),
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white_70,
        font_weight: 900,
        shadow_blur: LayoutScaler.widthFromDesign( 4 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );

    this.addChild( text );
  }

  private createUserIdText(): void
  {
    const design_text_h = 58;
    const design_copy_icon_w = 39;
    const design_copy_icon_h = 40;
    const design_copy_gap = 12;
    const design_text_size = 40;
    const design_min_text_size = 24;

    const local_player_id = AuthSessionStore.getPlayerId();
    const local_user_id = local_player_id != null
      ? String( local_player_id )
      : ( AnalyticStore.getUserId() || "" );
    const resolved_user_id = this.player_data?.user_id ?? local_user_id;
    const user_id_text = `ID: ${resolved_user_id || "—"}`;
    const font_size = LayoutScaler.textSizeFromDesign( design_text_size );
    const text_h = LayoutScaler.heightFromDesign( design_text_h );
    const text_y = this.rect.getH() + LayoutScaler.yFromDesign( 18 );
    const copy_icon_w = LayoutScaler.widthFromDesign( design_copy_icon_w );
    const copy_icon_h = LayoutScaler.heightFromDesign( design_copy_icon_h );
    const copy_gap = LayoutScaler.widthFromDesign( design_copy_gap );
    const measure_font = `${700} ${font_size}px ${TextStyleScheme.rubik}`;
    const prev_font = this.ctx.font;
    this.ctx.font = measure_font;
    const text_w = Math.ceil( this.ctx.measureText( user_id_text ).width ) + LayoutScaler.widthFromDesign( 8 );
    this.ctx.font = prev_font;
    const group_w = copy_icon_w + copy_gap + text_w;
    const group_x = this.rect.getW2() - group_w / 2;

    const text = new UITextElement(
      this.ctx,
      group_x + copy_icon_w + copy_gap,
      text_y,
      text_w,
      text_h,
      {
        text: user_id_text,
        font_size: font_size,
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white_70,
        font_weight: 900,
        auto_wrap: false,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( design_min_text_size ),
        shadow_blur: LayoutScaler.widthFromDesign( 4 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );

    const copy_icon = new UIImage(
      this.ctx,
      group_x,
      text_y + text_h / 2 - copy_icon_h / 2,
      copy_icon_w,
      copy_icon_h,
      ImgID.IconCopy,
      {},
      true
    );

    copy_icon.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      if ( resolved_user_id )
        void this.copyUserId( resolved_user_id );
    } );

    this.addChild( text );
    this.addChild( copy_icon );
  }

  private async copyUserId( user_id: string ): Promise<void>
  {
    try
    {
      if ( navigator.clipboard?.writeText )
      {
        await navigator.clipboard.writeText( user_id );
        return;
      }
    }
    catch ( err )
    {
      console.warn( "Clipboard API failed, using fallback:", err );
    }

    const textarea = document.createElement( "textarea" );
    textarea.value = user_id;
    textarea.setAttribute( "readonly", "" );
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.opacity = "0";
    document.body.appendChild( textarea );

    textarea.select();
    textarea.setSelectionRange( 0, textarea.value.length );

    try
    {
      if ( !document.execCommand( "copy" ) )
        console.warn( "Failed to copy user id with fallback" );
    }
    finally
    {
      textarea.remove();
    }
  }

  private createBackground(): void
  {
    const bg_w           = this.rect.getW();
    const bg_h           = this.rect.getH();
    this.bg = new UINineSliceElement(
      this.ctx,
      0,
      0,
      bg_w,
      bg_h,
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );

    this.addChild( this.bg );
  }

  private createInfoBlock(): void
  {
    const stats = StatisticsStorage.load();
    const level = this.player_data?.level ?? stats.level ?? 1;
    const points = this.player_data?.points ?? stats.points ?? 0;
    const days_in_game = this.resolveDaysInGame();

    const design_block_h = 300;
    const block_w = LayoutScaler.widthFromDesign( 800 );
    const block_h = LayoutScaler.heightFromDesign( design_block_h );
    const block_x = this.rect.getW2() - block_w / 2;
    const block_y = LayoutScaler.yFromDesign( 490.29 );

    const block = new RoundedRectangle(
      this.ctx,
      block_x,
      block_y,
      block_w,
      block_h,
      LayoutScaler.cornerRadiusFromDesign( 24 ),
      "#302CB2",
      undefined,
      false,
      undefined,
      0,
      {
        color: "rgba(255, 255, 255, 0.25)",
        offsetX: 0,
        offsetY: LayoutScaler.yFromDesign( 3 ),
        blur: LayoutScaler.widthFromDesign( 6 )
      },
      {
        color: "rgba(0, 0, 0, 0.31)",
        offsetX: 0,
        offsetY: LayoutScaler.yFromDesign( 3 ),
        blur: LayoutScaler.widthFromDesign( 6 )
      }
    );
    this.addChild( block );

    const rows: { label_key: string; value: string; with_star: boolean; fit_label?: boolean }[] = [
      { label_key: "PROFILE.LEVEL", value: String( level ), with_star: false, fit_label: true },
    ];
    if ( days_in_game !== null )
      rows.push( { label_key: "PROFILE.DAYS_IN_GAME", value: String( days_in_game ), with_star: false, fit_label: true } );
    // rows.push( { label_key: "PROFILE.STARS", value: this.formatNumber( points ), with_star: true } );

    const design_row_h = 62;
    const design_row_gap = 24;
    const total_rows_h = rows.length * design_row_h + ( rows.length - 1 ) * design_row_gap;
    const start_y = LayoutScaler.yFromDesign( 60 );

    rows.forEach( ( row, i ) =>
    {
      const row_y = start_y + i * ( design_row_h + design_row_gap );
      this.createInfoRow( block, row_y, row.label_key, row.value, row.with_star, row.fit_label ?? false );
    } );
  }

  private resolveDaysInGame(): number | null
  {
    if ( this.player_data?.days_in_game != null )
      return this.player_data.days_in_game;

    if ( this.player_data != null && this.player_data.editable === false )
      return null;

    const install_date = StatisticsStorage.load().install_date;
    if ( !install_date )
      return null;

    return Math.max( 0, Math.floor( ( Date.now() - install_date ) / 86_400_000 ) );
  }

  private createInfoRow(
    parent: RoundedRectangle,
    design_y: number,
    label_key: string,
    value: string,
    with_star = false,
    fit_label = false
  ): void
  {
    const row_h = LayoutScaler.heightFromDesign( 62 );
    const y = LayoutScaler.yFromDesign( design_y );

    parent.addChild( new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 45 ),
      y,
      LayoutScaler.widthFromDesign( 360 ),
      row_h,
      {
        localization_key: label_key,
        font_size: LayoutScaler.textSizeFromDesign( 48 ),
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        font_weight: 900,
        shadow_blur: LayoutScaler.widthFromDesign( 4 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
        ...( fit_label ? { scale_to_width: true, auto_wrap: false } : {} ),
      }
    ), true );

    const star_size = LayoutScaler.widthFromDesign( 48 );
    const right_pad = LayoutScaler.widthFromDesign( 45 );
    const star_gap = LayoutScaler.widthFromDesign( 10 );
    const value_w = LayoutScaler.widthFromDesign( 270 );
    const value_x = parent.getRect().getW() - value_w - right_pad
      - ( with_star ? star_size + star_gap : 0 );

    parent.addChild( new UITextElement(
      this.ctx,
      value_x,
      y,
      value_w,
      row_h,
      {
        text: value,
        font_size: LayoutScaler.textSizeFromDesign( 48 ),
        text_align: MyTextAlign.Right,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        font_weight: 900,
        shadow_blur: LayoutScaler.widthFromDesign( 4 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    ), true );

    if ( with_star )
    {
      parent.addChild( new UIImage(
        this.ctx,
        parent.getRect().getW() - right_pad - star_size,
        y + row_h / 2 - star_size / 2,
        star_size,
        star_size,
        ImgID.ProfileStarIcon
      ), true );
    }

  }

  private formatNumber( value: number ): string
  {
    return Math.max( 0, Math.floor( value ) ).toLocaleString( "en-US" ).replace( /,/g, " " );
  }

  private createSingleInfoBlock( localization_key: string, value: string, block_y: number ): void
  {
    const design_block_w     = 721;
    const design_block_h     = 80;
    const design_block_radius = 10;
    const design_text_offset_x = 22;
    const design_text_right_pad = 30;
    const design_text_size   = 40;

    const block_w  = LayoutScaler.widthFromDesign( design_block_w );
    const block_h  = LayoutScaler.heightFromDesign( design_block_h );
    const block_x  = this.bg.getRect().getW2() - block_w / 2;

    const block = new RoundedRectangle(
      this.ctx,
      block_x,
      block_y,
      block_w,
      block_h,
      LayoutScaler.cornerRadiusFromDesign( design_block_radius ),
      ColorScheme.black,
    );

    const text_left = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_text_offset_x ),
      0,
      block_w / 2,
      block_h,
      {
        localization_key: localization_key,
        font_size: LayoutScaler.textSizeFromDesign( design_text_size ),
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.black,
        font_weight: 900,
        using_y_offset: false,
      }
    );

    const text_right = new UITextElement(
      this.ctx,
      block_w / 2,
      0,
      block_w / 2 - LayoutScaler.widthFromDesign( design_text_right_pad ),
      block_h,
      {
        text: value,
        font_size: LayoutScaler.textSizeFromDesign( design_text_size ),
        text_align: MyTextAlign.Right,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.black,
        font_weight: 900,
        using_y_offset: false,
      }
    );

    block.addChild( text_left );
    block.addChild( text_right );
    this.addChild( block );
  }

  private createIconButtons(): void
  {
    const design_icon_size = 120;
    const design_gap       = 52;
    const design_y         = 775;
    const design_border_w  = 4;

    const icon_size = LayoutScaler.widthFromDesign( design_icon_size );
    const gap       = LayoutScaler.widthFromDesign( design_gap );
    const y         = LayoutScaler.yFromDesign( design_y );
    const border_w  = LayoutScaler.widthFromDesign( design_border_w );

    const icons: { img: ImgID; onPress: () => void }[] = [];

    if ( !hasLinkedSocialProvider( LinkedSocialProvider.Facebook ) )
    {
      icons.push( { img: ImgID.FacebookIcon, onPress: () => {
        const linked = getLinkedSocialProviders();
        DialogManager.openDialog( linked.length > 0 ? new AccountProgressSavedDialog() : new SaveDialog() );
      } } );
    }

    // icons.push( { img: ImgID.IconAccount, onPress: () => {
    //   const linked = getLinkedSocialProviders();
    //   DialogManager.openDialog( linked.length > 0 ? new AccountProgressSavedDialog() : new SaveDialog() );
    // } } );

    const total_w = icon_size * icons.length + gap * ( icons.length - 1 );
    const start_x = ( this.rect.getW() - total_w ) / 2;

    icons.forEach( ( item, index ) =>
    {
      const x = start_x + index * ( icon_size + gap );

      const btn_bg = new RoundedRectangle(
        this.ctx,
        x,
        y,
        icon_size,
        icon_size,
        icon_size / 2,
        ColorScheme.white,
        undefined,
        true,
        ColorScheme.black,
        border_w
      );

      btn_bg.setOnClick( item.onPress );

      const img = new UIImage( this.ctx, 0, 0, icon_size, icon_size, item.img, {}, true );
      img.setClickable( false );

      btn_bg.addChild( img );
      this.addChild( btn_bg );
    } );
  }

  private createLines(): void
  {
    const design_line_w = 721;
    const design_line_y_1 = 384;
    const design_line_y_3 = 725;

    const line_h  = this.lines_h;
    const line_w  = LayoutScaler.widthFromDesign( design_line_w );
    const line_x  = this.bg.getRect().getW2() - line_w / 2;
    const line_y_1 = LayoutScaler.yFromDesign( design_line_y_1 );
    const line_y_3 = LayoutScaler.yFromDesign( design_line_y_3 );

    this.addChild( new RoundedRectangle( this.ctx, line_x, line_y_1, line_w, line_h, 0, ColorScheme.black ) );
    this.addChild( new RoundedRectangle( this.ctx, line_x, line_y_3, line_w, line_h, 0, ColorScheme.black ) );
  }

  private createCloseButton(): void
  {
    const design_btn_size = 117;

    const btn_size = LayoutScaler.widthFromDesign( design_btn_size );
    const btn_x = this.rect.getW() - btn_size + LayoutScaler.widthFromDesign( 25 );
    const btn_y = -LayoutScaler.yFromDesign( 40 );

    this.close_btn = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_size,
      btn_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );

    this.close_btn.setOnClick( () => DialogManager.closeDialog() );
    this.addChild( this.close_btn );
  }

  protected override playAppearAnimation(
    from_scale      = 0.7,
    overshoot_scale = 1.1,
    final_scale     = 1.0,
    scale_up_duration = 136,
    settle_duration   = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );

    super.playAppearAnimation( from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  private createPlayerNickname(): void
  {
    const design_nickname_w      = 800;
    const design_nickname_h      = 109.42;
    const design_nickname_y      = 310.29;
    const design_nickname_radius = 24;
    const design_text_size       = 48;

    const nickname_w = LayoutScaler.widthFromDesign( design_nickname_w );
    const nickname_h = LayoutScaler.heightFromDesign( design_nickname_h );
    const nickname_x = this.rect.getW2() - nickname_w / 2;
    const nickname_y = LayoutScaler.yFromDesign( design_nickname_y );

    const bg = new RoundedRectangle(
      this.ctx,
      nickname_x,
      nickname_y,
      nickname_w,
      nickname_h,
      LayoutScaler.cornerRadiusFromDesign( design_nickname_radius ),
      "#302CB2",
      undefined,
      true,
      undefined,
      0,
      {
        color: "rgba(255, 255, 255, 0.25)",
        offsetX: 0,
        offsetY: LayoutScaler.yFromDesign( 3 ),
        blur: LayoutScaler.widthFromDesign( 6 )
      },
      {
        color: "rgba(0, 0, 0, 0.31)",
        offsetX: 0,
        offsetY: LayoutScaler.yFromDesign( 3 ),
        blur: LayoutScaler.widthFromDesign( 6 )
      }
    );

    let last_name = this.player_data?.name
      ?? localStorage.getItem( ProfileDialog.NICKNAME_KEY )
      ?? "Player";
    const editable = this.player_data?.editable ?? true;
    const open_edit_name = () =>
    {
      DialogManager.openDialog( new EditNameDialog( last_name, ( new_name: string ) =>
      {
        localStorage.setItem( ProfileDialog.NICKNAME_KEY, new_name );
        this.name_input.setText( new_name );
        last_name = new_name;
      } ) );
    };

    this.name_input = new UITextElement(
      this.ctx,
      0,
      0,
      nickname_w,
      nickname_h,
      {
        text:           last_name,
        text_style:     TextStyleScheme.rubik,
        text_align:     MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_color:     ColorScheme.white,
        font_size:      LayoutScaler.textSizeFromDesign( design_text_size ),
        font_weight:    900,
        shadow_blur:    LayoutScaler.widthFromDesign( 4 ),
        shadow_color:   ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );

    if ( editable )
      bg.setOnClick( open_edit_name );
    else
      bg.setClickable( false );

    bg.addChild( this.name_input );
    this.addChild( bg );

    if ( editable )
    {
      const edit_size = LayoutScaler.widthFromDesign( 75.23 );
      const edit_overlap_x = LayoutScaler.widthFromDesign( 49.51 );
      const edit_y = LayoutScaler.yFromDesign( 281 );
      this.createEditButton(
        nickname_x + nickname_w - edit_overlap_x,
        edit_y,
        edit_size,
        open_edit_name
      );
    }
  }

  public override onDialogWillClose(): void
  {
  }

  public update( now: number ): void
  {
    super.update( now );
  }

  public name(): string
  {
    return "ProfileDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.startRender();

    this.finishRender();
  }
}
